<template>
  <!-- What the user sent: plain text, and the files they attached -->
  <div class="user-message">
    <span v-if="message.content">{{ message.content }}</span>

    <div v-if="files.length > 0" class="message-attachments">
      <div
        v-for="file in files"
        :key="file.id"
        class="attachment-chip"
        :class="{
          'attachment-chip--clickable': file.url && file.state.tone !== 'busy',
        }"
        v-tooltip="file.state.tooltip"
        @click="handleAttachmentClick(file)"
      >
        <i :class="getFileIcon(file.mime_type)" class="attachment-icon"></i>
        <div class="attachment-info">
          <span class="attachment-name">{{ file.original_file_name }}</span>
          <span
            class="attachment-size"
            :class="`attachment-size--${file.state.tone}`"
            >{{ file.state.label }}</span
          >
        </div>
        <status-spinner
          v-if="file.state.tone === 'busy'"
          :size="14"
          class="attachment-tail"
        />
        <i v-else-if="file.url" class="fas fa-download download-icon"></i>
        <div v-if="file.state.progress !== null" class="attachment-progress">
          <span
            :style="{ width: `${Math.round(file.state.progress * 100)}%` }"
          ></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusSpinner from "@/components/OliverBotV2/shared/StatusSpinner.vue";
import { chipState } from "@/components/OliverBotV2/attachments/attachmentStatus.js";
import { getFileIcon } from "@/utils/fileHelpers.js";

export default {
  name: "UserMessage",
  components: { StatusSpinner },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // A file sent while still being read keeps its live state here, as it had in the
    // composer; the message carries only what it was sent with.
    files() {
      const live = this.$store.state.oliverChatBotV2.attachments;
      return (this.message.attachments || []).map((attachment) => {
        const file = { ...attachment, ...(live[attachment.id] || {}) };
        return { ...file, state: chipState(file) };
      });
    },
  },
  methods: {
    handleAttachmentClick(file) {
      if (file.url && file.state.tone !== "busy") {
        window.open(file.url, "_blank");
      }
    },
    getFileIcon,
  },
};
</script>

<style lang="scss" scoped>
.user-message {
  white-space: pre-wrap;
  position: relative;
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.attachment-chip {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--oliver-assistant-bg);
  border: 1px solid var(--oliver-border);
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(143, 138, 230, 0.2);
  }

  // Clickable attachment with download link
  &--clickable {
    cursor: pointer;

    &:hover {
      background: var(--oliver-accent-light);
      border-color: var(--oliver-accent-border);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px var(--oliver-indicator-border);

      .download-icon {
        color: var(--oliver-accent);
      }
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// A file still being read: its line, a spinner at the end and a bar along the bottom.
.attachment-size--part {
  color: #c05621;
}

.attachment-size--bad {
  color: #e53e3e;
}

.attachment-tail {
  margin-left: auto;
}

.attachment-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(99, 179, 237, 0.15);

  span {
    display: block;
    height: 100%;
    background: var(--oliver-accent);
    transition: width 0.2s ease;
  }
}

.attachment-icon {
  font-size: 18px;
  color: var(--oliver-accent);
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.attachment-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--oliver-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-size {
  font-size: 11px;
  color: var(--oliver-text-muted);
}

.download-icon {
  font-size: 12px;
  color: var(--oliver-accent);
  margin-left: 4px;
  flex-shrink: 0;
  transition: color 0.2s ease;
}

@media (max-width: 768px) {
  .message-attachments {
    margin-top: 10px;
    gap: 6px;
  }

  .attachment-chip {
    padding: 8px 10px;
    gap: 8px;
  }

  .attachment-icon {
    font-size: 16px;
  }

  .attachment-name {
    font-size: 12px;
  }

  .attachment-size {
    font-size: 10px;
  }
}
</style>
